import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useGetModel } from '../../../../../api/routes/models'
import {
    useGetModelAttributes,
    useCreateModelAttribute,
    useUpdateModelAttribute,
    useDeleteModelAttribute,
    useUpsertCanField,
    useDeleteCanField,
} from '../../../../../api/routes/model-attributes'
import { authClient, hasPermission } from '../../../../../lib/auth-client'
import { Button } from '../../../../../components/ui/button'
import { Badge } from '../../../../../components/ui/badge'
import {
    ArrowLeft,
    Plus,
    Pencil,
    Trash2,
    Settings2,
    ChevronDown,
    ChevronRight,
    AlertTriangle,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { AttributeDialog, AttributeFormData } from '../attribute-dialog'
import { CanConfigDialog, CanFieldFormData } from '../can-config-dialog'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../../components/ui/dialog'

const operatorSymbols: Record<string, string> = {
    eq: '=',
    ne: '!=',
    gt: '>',
    lt: '<',
    ge: '>=',
    le: '<=',
}

const dataTypeBadgeVariant = (dataType: string) => {
    switch (dataType) {
        case 'number':
            return 'default' as const
        case 'boolean':
            return 'secondary' as const
        default:
            return 'outline' as const
    }
}

const CanFrameVisual = ({ canField }: { canField: any }) => {
    const startByte = canField.start_byte
    const byteCount = canField.byte_count || 1
    const startBit = canField.start_bit ?? 0
    const bitCount = canField.bit_count ?? 0
    const hasBitDetail = bitCount > 0

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>
                    CAN ID:{' '}
                    <span className="font-mono font-medium text-foreground">
                        {canField.can_id}
                    </span>
                </span>
                <span className="text-border">|</span>
                <span>
                    Bytes {startByte}
                    {byteCount > 1 ? `-${startByte + byteCount - 1}` : ''}
                </span>
                {hasBitDetail && (
                    <>
                        <span className="text-border">|</span>
                        <span>
                            Bits {startBit}
                            {bitCount > 1 ? `-${startBit + bitCount - 1}` : ''}
                        </span>
                    </>
                )}
            </div>

            {/* Byte-level view */}
            <div>
                <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">
                    Bytes
                </div>
                <div className="flex gap-0.5">
                    {Array.from({ length: 8 }, (_, i) => {
                        const isActive =
                            i >= startByte && i < startByte + byteCount
                        return (
                            <div
                                key={i}
                                className={`h-8 flex-1 rounded-sm flex items-center justify-center text-xs font-mono transition-colors ${
                                    isActive
                                        ? 'bg-primary/15 border border-primary/30 text-primary font-medium'
                                        : 'bg-muted/50 border border-border/50 text-muted-foreground/50'
                                }`}
                            >
                                {i}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Bit-level view within active bytes */}
            {hasBitDetail && (
                <div>
                    <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">
                        Bit Layout (Byte{byteCount > 1 ? 's' : ''} {startByte}
                        {byteCount > 1 ? `-${startByte + byteCount - 1}` : ''})
                    </div>
                    <div className="flex gap-px">
                        {Array.from({ length: byteCount * 8 }, (_, i) => {
                            const isActive =
                                i >= startBit && i < startBit + bitCount
                            return (
                                <div
                                    key={i}
                                    className={`h-6 flex-1 flex items-center justify-center text-[9px] font-mono transition-colors ${
                                        isActive
                                            ? 'bg-emerald-100 border border-emerald-300 text-emerald-700 font-medium'
                                            : 'bg-muted/30 border border-border/30 text-muted-foreground/40'
                                    } ${i % 8 === 0 && i > 0 ? 'ml-1' : ''}`}
                                >
                                    {i}
                                </div>
                            )
                        })}
                    </div>
                    <div className="flex mt-0.5">
                        {Array.from({ length: byteCount }, (_, byteIdx) => (
                            <div
                                key={byteIdx}
                                className={`flex-1 text-center text-[9px] text-muted-foreground ${byteIdx > 0 ? 'ml-1' : ''}`}
                            >
                                Byte {startByte + byteIdx}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex gap-4 text-xs text-muted-foreground">
                <span>
                    Send Frequency:{' '}
                    <span className="text-foreground">
                        {canField.frequency}ms
                    </span>
                </span>
                <span>
                    Update Frequency:{' '}
                    <span className="text-foreground">
                        {canField.update_frequency}ms
                    </span>
                </span>
            </div>
        </div>
    )
}

export const ModelAttributesRoute = () => {
    const { id: modelId } = useParams<{ id: string }>()
    const { data: model, isLoading: modelLoading } = useGetModel(modelId!)
    const { data: attributes, isLoading: attributesLoading } =
        useGetModelAttributes(modelId!)

    const createAttribute = useCreateModelAttribute(modelId!)
    const updateAttribute = useUpdateModelAttribute(modelId!)
    const deleteAttribute = useDeleteModelAttribute(modelId!)
    const upsertCanField = useUpsertCanField(modelId!)
    const deleteCanField = useDeleteCanField(modelId!)

    const { data: session } = authClient.useSession()
    const role = (session?.user as any)?.role
    const canConfigure = role && hasPermission(role, { model: ['configure'] })
    const canDelete = role && hasPermission(role, { model: ['delete'] })

    const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(
        new Set()
    )
    const [attributeDialogOpen, setAttributeDialogOpen] = useState(false)
    const [editingAttribute, setEditingAttribute] = useState<any>(null)
    const [canDialogOpen, setCanDialogOpen] = useState(false)
    const [canDialogAttribute, setCanDialogAttribute] = useState<any>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deletingAttribute, setDeletingAttribute] = useState<any>(null)

    const toggleExpanded = (id: string) => {
        setExpandedAttributes((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const handleSaveAttribute = (data: AttributeFormData) => {
        const payload: any = {
            name: data.name.trim(),
            friendlyName: data.friendlyName.trim() || null,
            dataType: data.dataType,
            unit: data.unit.trim() || null,
            min: data.min !== '' ? Number(data.min) : null,
            max: data.max !== '' ? Number(data.max) : null,
            positiveSuffix: data.positiveSuffix.trim() || null,
            negativeSuffix: data.negativeSuffix.trim() || null,
            trueValue: data.trueValue.trim() || null,
            falseValue: data.falseValue.trim() || null,
            trueDataValue: data.trueDataValue.trim() || null,
            falseDataValue: data.falseDataValue.trim() || null,
        }

        const onError = (error: any) => {
            toast.error(
                error?.response?.data?.message ||
                    error.message ||
                    'Failed to save attribute'
            )
        }

        if (editingAttribute) {
            updateAttribute.mutate(
                { attributeId: editingAttribute.uuid, data: payload },
                {
                    onSuccess: () => {
                        toast.success('Attribute updated')
                        setAttributeDialogOpen(false)
                        setEditingAttribute(null)
                    },
                    onError,
                }
            )
        } else {
            createAttribute.mutate(payload, {
                onSuccess: () => {
                    toast.success('Attribute created')
                    setAttributeDialogOpen(false)
                },
                onError,
            })
        }
    }

    const handleSaveCanField = (data: CanFieldFormData) => {
        if (!canDialogAttribute) return

        const payload = {
            can_id: data.can_id.trim(),
            start_byte: Number(data.start_byte),
            byte_count: Number(data.byte_count),
            start_bit: Number(data.start_bit),
            bit_count: Number(data.bit_count),
            frequency: Number(data.frequency),
            update_frequency: Number(data.update_frequency),
            criticalStates: data.criticalStates.map((cs) => ({
                value: Number(cs.value),
                operator: cs.operator,
            })),
        }

        upsertCanField.mutate(
            { attributeId: canDialogAttribute.uuid, data: payload },
            {
                onSuccess: () => {
                    toast.success('CAN field configuration saved')
                    setCanDialogOpen(false)
                    setCanDialogAttribute(null)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to save CAN configuration'
                    )
                },
            }
        )
    }

    const handleDeleteAttribute = () => {
        if (!deletingAttribute) return
        deleteAttribute.mutate(deletingAttribute.uuid, {
            onSuccess: () => {
                toast.success('Attribute deleted')
                setDeleteDialogOpen(false)
                setDeletingAttribute(null)
            },
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message ||
                        error.message ||
                        'Failed to delete attribute'
                )
            },
        })
    }

    const handleRemoveCanField = (attr: any) => {
        deleteCanField.mutate(attr.uuid, {
            onSuccess: () => toast.success('CAN field removed'),
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message ||
                        error.message ||
                        'Failed to remove CAN field'
                )
            },
        })
    }

    if (modelLoading || attributesLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Loading...
            </div>
        )
    }

    if (!model) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Model not found.
            </div>
        )
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="mb-6">
                <Link
                    to={`/models/${modelId}`}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to {model.name}
                </Link>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                            Attributes
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Configure model attributes and CAN field mappings.
                        </p>
                    </div>
                    {canConfigure && (
                        <Button
                            onClick={() => {
                                setEditingAttribute(null)
                                setAttributeDialogOpen(true)
                            }}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Attribute
                        </Button>
                    )}
                </div>
            </div>

            {!attributes || attributes.length === 0 ? (
                <div className="rounded-lg border bg-card p-12 text-center">
                    <p className="text-muted-foreground">
                        No attributes configured for this model yet.
                    </p>
                    {canConfigure && (
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => {
                                setEditingAttribute(null)
                                setAttributeDialogOpen(true)
                            }}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Attribute
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {attributes.map((attr: any) => {
                        const isExpanded = expandedAttributes.has(attr.uuid)
                        const hasCanField = !!attr.canField
                        const criticalStates =
                            attr.canField?.criticalStates || []

                        return (
                            <div
                                key={attr.uuid}
                                className="rounded-lg border bg-card overflow-hidden"
                            >
                                <div
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                                    onClick={() => toggleExpanded(attr.uuid)}
                                >
                                    {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm truncate">
                                                {attr.friendlyName || attr.name}
                                            </span>
                                            {attr.friendlyName && (
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    {attr.name}
                                                </span>
                                            )}
                                            <Badge
                                                variant={dataTypeBadgeVariant(
                                                    attr.dataType
                                                )}
                                                className="text-[10px] px-1.5 py-0"
                                            >
                                                {attr.dataType}
                                            </Badge>
                                            {attr.unit && (
                                                <span className="text-xs text-muted-foreground">
                                                    ({attr.unit})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center gap-1.5 shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {!hasCanField && (
                                            <span className="text-xs text-amber-600 flex items-center gap-1 mr-2">
                                                <AlertTriangle className="h-3 w-3" />
                                                No CAN config
                                            </span>
                                        )}
                                        {canConfigure && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setCanDialogAttribute(
                                                            attr
                                                        )
                                                        setCanDialogOpen(true)
                                                    }}
                                                    title="Configure CAN field"
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                >
                                                    <Settings2 className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingAttribute(
                                                            attr
                                                        )
                                                        setAttributeDialogOpen(
                                                            true
                                                        )
                                                    }}
                                                    title="Edit attribute"
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                        {(canDelete || canConfigure) && (
                                            <button
                                                onClick={() => {
                                                    setDeletingAttribute(attr)
                                                    setDeleteDialogOpen(true)
                                                }}
                                                title="Delete attribute"
                                                className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="border-t px-4 py-4 space-y-4 bg-muted/10">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                    Name
                                                </span>
                                                <span className="font-mono">
                                                    {attr.name}
                                                </span>
                                            </div>
                                            {attr.friendlyName && (
                                                <div>
                                                    <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                        Friendly Name
                                                    </span>
                                                    <span>
                                                        {attr.friendlyName}
                                                    </span>
                                                </div>
                                            )}
                                            <div>
                                                <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                    Data Type
                                                </span>
                                                <span>{attr.dataType}</span>
                                            </div>
                                            {attr.unit && (
                                                <div>
                                                    <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                        Unit
                                                    </span>
                                                    <span>{attr.unit}</span>
                                                </div>
                                            )}
                                            {attr.min != null && (
                                                <div>
                                                    <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                        Min
                                                    </span>
                                                    <span>{attr.min}</span>
                                                </div>
                                            )}
                                            {attr.max != null && (
                                                <div>
                                                    <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                        Max
                                                    </span>
                                                    <span>{attr.max}</span>
                                                </div>
                                            )}
                                            {attr.dataType === 'boolean' &&
                                                attr.trueValue && (
                                                    <div>
                                                        <span className="text-xs text-muted-foreground uppercase tracking-wide block">
                                                            True / False
                                                        </span>
                                                        <span>
                                                            {attr.trueValue} /{' '}
                                                            {attr.falseValue}
                                                        </span>
                                                    </div>
                                                )}
                                        </div>

                                        {hasCanField ? (
                                            <div className="rounded-md border bg-background p-3 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                                        CAN Field Configuration
                                                    </span>
                                                    {canConfigure && (
                                                        <button
                                                            onClick={() =>
                                                                handleRemoveCanField(
                                                                    attr
                                                                )
                                                            }
                                                            className="text-xs text-red-500 hover:text-red-700 transition-colors"
                                                        >
                                                            Remove CAN Config
                                                        </button>
                                                    )}
                                                </div>
                                                <CanFrameVisual
                                                    canField={attr.canField}
                                                />

                                                {criticalStates.length > 0 && (
                                                    <div className="pt-2 border-t space-y-1">
                                                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                                            Critical States
                                                        </span>
                                                        <div className="flex flex-wrap gap-2">
                                                            {criticalStates.map(
                                                                (
                                                                    cs: any,
                                                                    i: number
                                                                ) => (
                                                                    <span
                                                                        key={i}
                                                                        className="inline-flex items-center gap-1 rounded-md bg-red-50 border border-red-200 px-2 py-0.5 text-xs text-red-700 font-mono"
                                                                    >
                                                                        {operatorSymbols[
                                                                            cs
                                                                                .operator
                                                                        ] ||
                                                                            cs.operator}{' '}
                                                                        {
                                                                            cs.value
                                                                        }
                                                                    </span>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center">
                                                <p className="text-sm text-muted-foreground">
                                                    No CAN field configured.
                                                </p>
                                                {canConfigure && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={() => {
                                                            setCanDialogAttribute(
                                                                attr
                                                            )
                                                            setCanDialogOpen(
                                                                true
                                                            )
                                                        }}
                                                    >
                                                        <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                                                        Configure CAN Field
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            <AttributeDialog
                open={attributeDialogOpen}
                onOpenChange={(open) => {
                    setAttributeDialogOpen(open)
                    if (!open) setEditingAttribute(null)
                }}
                onSave={handleSaveAttribute}
                initialData={editingAttribute}
                isPending={
                    createAttribute.isPending || updateAttribute.isPending
                }
            />

            <CanConfigDialog
                open={canDialogOpen}
                onOpenChange={(open) => {
                    setCanDialogOpen(open)
                    if (!open) setCanDialogAttribute(null)
                }}
                onSave={handleSaveCanField}
                initialData={canDialogAttribute?.canField}
                isPending={upsertCanField.isPending}
                attributeName={
                    canDialogAttribute?.friendlyName ||
                    canDialogAttribute?.name ||
                    ''
                }
            />

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Attribute</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{' '}
                            <span className="font-semibold">
                                {deletingAttribute?.friendlyName ||
                                    deletingAttribute?.name}
                            </span>
                            ? This will also remove its CAN field configuration
                            and critical states.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAttribute}
                            disabled={deleteAttribute.isPending}
                        >
                            {deleteAttribute.isPending
                                ? 'Deleting...'
                                : 'Delete Attribute'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
